import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../App';
import { Star, Award, Trophy, Target, ArrowRight, CheckCircle } from 'lucide-react';

export default function ProCoachingPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-yellow-950/50 to-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600/20 border border-yellow-600/40 rounded-full mb-6">
            <Star className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-bold uppercase tracking-wider">Pro Coaching</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-heading font-bold mb-6">
            Learn from the Pros
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
            Get expert guidance from Texas natives with decades of winning experience at the tables.
          </p>
        </div>
      </section>

      {/* Coaches */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-heading font-bold text-center mb-16">
            Meet Your Coaches
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                name: 'Tom "Texas Tom" Henderson',
                title: '20+ Years Experience',
                specialty: 'Cash Games & Bankroll Management',
                image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop'
              },
              {
                name: 'Diane "Dallas D" Martinez',
                title: 'Tournament Expert',
                specialty: 'Position Play & Reading Opponents',
                image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop'
              },
              {
                name: 'Aaron "Austin Ace" Wilson',
                title: 'Math & Strategy',
                specialty: 'Odds Calculation & Expected Value',
                image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop'
              }
            ].map((coach, idx) => (
              <Link
                to="/booking"
                key={idx}
                className="bg-card border border-border rounded-2xl p-8 text-center hover:border-primary/50 transition-all cursor-pointer"
                data-testid={`coach-card-${idx}`}
              >
                <div className="relative inline-block mb-6">
                  <img
                    src={coach.image}
                    alt={coach.name}
                    className="w-32 h-32 rounded-full border-4 border-primary/20 object-cover"
                  />
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center border-4 border-card">
                    <Star className="w-5 h-5 text-black fill-black" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">{coach.name}</h3>
                <p className="text-primary font-semibold text-sm mb-2">{coach.title}</p>
                <p className="text-muted-foreground text-sm">{coach.specialty}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-card/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-heading font-bold text-center mb-12">
            What's Included
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/features/live-sessions" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="coaching-live-sessions">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Live Coaching Sessions</h3>
                  <p className="text-muted-foreground">Watch pros analyze real hands and explain their thought process step-by-step.</p>
                </div>
              </div>
            </Link>

            <Link to="/community" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="coaching-hand-reviews">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Hand Reviews</h3>
                  <p className="text-muted-foreground">Submit your hands for detailed analysis and personalized feedback from coaches.</p>
                </div>
              </div>
            </Link>

            <Link to="/lessons" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="coaching-strategy-workshops">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Strategy Workshops</h3>
                  <p className="text-muted-foreground">Deep-dive sessions on specific topics like tournament play, cash games, and more.</p>
                </div>
              </div>
            </Link>

            <Link to="/features/live-sessions" className="bg-card border border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-all" data-testid="coaching-direct-access">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Direct Access</h3>
                  <p className="text-muted-foreground">Ask questions during live sessions and get answers directly from the pros.</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-heading font-bold mb-6">
            Learn from the Best
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Get expert coaching and take your game to the next level.
          </p>
          <Link
            to="/membership"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-black px-10 py-5 rounded-xl text-lg font-bold shadow-lg transition-all"
          >
            Start Learning
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
